<header
  [class]="
    cn(
      'site-gutter left-0 right-0 top-0 z-40 transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none',
      isDark ? 'fixed pt-6' : 'sticky pt-4',
      hidden() ? 'translate-y-[-140%]' : 'translate-y-0'
    )
  "
>
  <div class="site-gutter__track">
    <nav
      [attr.aria-label]="'nav.mainAriaLabel' | transloco"
      [class]="
        cn(
          'site-gutter__inset flex w-full items-center justify-between rounded-full border transition-all duration-300',
          '[backdrop-filter:blur(28px)_saturate(180%)] [-webkit-backdrop-filter:blur(28px)_saturate(180%)]',
          isDark
            ? 'border-white/10 bg-ink/55 py-3 text-ink-foreground shadow-[0_8px_30px_-12px_rgba(0,0,0,0.4)]'
            : 'border-black/6 bg-white/72 py-2.5 shadow-[0_1px_3px_rgba(0,0,0,0.04),0_8px_28px_-10px_rgba(0,0,0,0.10)]'
        )
      "
    >
      <a
        [routerLink]="'/' | localizedRouterLink"
        [class]="
          cn(
            'flex items-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
            isDark ? 'text-white focus-visible:ring-offset-ink' : 'text-ink focus-visible:ring-offset-background'
          )
        "
        [attr.aria-label]="'common.homeAriaLabel' | transloco"
      >
        <app-infogov-logo [className]="isDark ? 'h-7' : 'h-6'" />
      </a>

      <app-desktop-nav [items]="navItems" [isDark]="isDark" />

      <div class="flex items-center gap-2">
        <app-language-switcher [isDark]="isDark" />
        <app-mobile-nav [isDark]="isDark" (menuRequested)="openMobile()" />
      </div>
    </nav>
  </div>
</header>

@if (mobileOpen()) {
  <div class="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true">
    <button
      type="button"
      class="absolute inset-0 bg-black/20"
      [attr.aria-label]="'common.closeMenu' | transloco"
      (click)="closeMobile()"
    ></button>

    <div
      class="absolute inset-y-0 right-0 flex w-[88vw] max-w-sm flex-col border-l border-black/6 bg-white shadow-elevated"
    >
      <div class="flex h-14 items-center justify-between border-b border-black/6 px-5">
        <a
          [routerLink]="'/' | localizedRouterLink"
          (click)="closeMobile()"
          [attr.aria-label]="'common.homeAriaLabel' | transloco"
          class="flex items-center"
        >
          <app-infogov-logo className="h-6" />
        </a>
        <button
          type="button"
          [attr.aria-label]="'common.closeMenu' | transloco"
          class="inline-flex h-9 w-9 items-center justify-center rounded-full text-foreground/70 hover:bg-black/5"
          (click)="closeMobile()"
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="h-5 w-5"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      </div>

      <nav
        [attr.aria-label]="'nav.mobileAriaLabel' | transloco"
        class="flex flex-1 flex-col overflow-y-auto px-2 py-3"
      >
        @for (item of navItems; track item.labelKey) {
          @if (item.kind === 'link') {
            @if (item.path && !item.disabled) {
              <a
                [routerLink]="item.path | localizedRouterLink"
                routerLinkActive="text-primary"
                [routerLinkActiveOptions]="{ exact: item.path === '/' }"
                (click)="closeMobile()"
                class="rounded-xl px-3 py-3 text-[15px] font-medium text-foreground/85 transition-colors hover:bg-black/4"
              >
                {{ item.labelKey | transloco }}
              </a>
            } @else {
              <span
                aria-disabled="true"
                class="cursor-not-allowed rounded-xl px-3 py-3 text-[15px] font-medium text-foreground/45"
              >
                {{ item.labelKey | transloco }}
              </span>
            }
          } @else {
            <div class="flex flex-col">
              <button
                type="button"
                [attr.aria-expanded]="mobileOpenGroup() === item.labelKey"
                (click)="toggleMobileGroup(item.labelKey)"
                class="flex items-center justify-between rounded-xl px-3 py-3 text-[15px] font-medium text-foreground/85 transition-colors hover:bg-black/4"
              >
                {{ item.labelKey | transloco }}
                <svg
                  aria-hidden="true"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  [class]="
                    cn(
                      'h-4 w-4 transition-transform',
                      mobileOpenGroup() === item.labelKey && 'rotate-180'
                    )
                  "
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </button>

              @if (mobileOpenGroup() === item.labelKey) {
                <div class="ml-3 flex flex-col border-l border-black/6 pl-3">
                  @for (sub of item.items; track sub.labelKey) {
                    @if (sub.path && !sub.disabled) {
                      <a
                        [routerLink]="sub.path | localizedRouterLink"
                        (click)="closeMobile()"
                        class="rounded-lg px-3 py-2.5 text-[14px] text-foreground/75 transition-colors hover:bg-black/4 hover:text-primary"
                      >
                        {{ sub.labelKey | transloco }}
                      </a>
                    } @else {
                      <span
                        aria-disabled="true"
                        class="cursor-not-allowed rounded-lg px-3 py-2.5 text-[14px] text-foreground/40"
                      >
                        {{ sub.labelKey | transloco }}
                      </span>
                    }
                  }
                </div>
              }
            </div>
          }
        }
      </nav>

      <app-language-switcher presentation="pills" />
    </div>
  </div>
}
